<template>
  <v-row class="fill-height program-event-page">
    <v-col>
      <div class="program-event-shell">
        <div class="program-event-hero">
          <div class="program-event-hero__text">
            <div class="program-event-hero__title">Program Events</div>
            <div class="program-event-hero__subtitle">
              Track CPD activities, providers, and schedules at a glance.
            </div>
          </div>
          <div class="program-event-hero__filter">
            <v-autocomplete
                v-model="regionValue"
                :items="regions"
                :loading="regionLoading"
                item-text="name"
                item-value="id"
                clearable
                dense
                outlined
                hide-details
                label="Filter by Region"
                prepend-inner-icon="mdi-map-marker-radius"
                class="program-event-filter"
            />
            <v-autocomplete
                v-model="cpdProviderValue"
                :items="cpdProviders"
                :loading="cpdProviderLoading"
                item-text="trainingCenterName"
                item-value="id"
                clearable
                dense
                outlined
                hide-details
                label="Filter by CPD Provider"
                prepend-inner-icon="mdi-filter-variant"
                class="program-event-filter"
            />
            <v-select
                v-model="yearValue"
                :items="yearOptions"
                dense
                outlined
                hide-details
                label="Year"
                prepend-inner-icon="mdi-calendar"
                class="program-event-filter program-event-filter--year"
            />
          </div>
        </div>
        <v-toolbar class="program-event-toolbar" flat>
          <v-btn class="mr-4" color="primary" outlined @click="setToday">
            Today
          </v-btn>
          <v-btn color="primary" fab small text @click="prev">
            <v-icon small> mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn color="primary" fab small text @click="next">
            <v-icon small> mdi-chevron-right</v-icon>
          </v-btn>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  text
                  class="program-event-toolbar__title-btn"
                  v-bind="attrs"
                  v-on="on"
              >
                {{ currentMonthLabel }}
                <v-icon right small>mdi-menu-down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                  v-for="(month, index) in monthOptions"
                  :key="month"
                  @click="monthValue = index"
              >
                <v-list-item-title>{{ month }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-spacer></v-spacer>
          <v-menu bottom right>
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="primary" outlined v-bind="attrs" v-on="on">
                <span>{{ typeToLabel[type] }}</span>
                <v-icon right> mdi-menu-down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="type = 'day'">
                <v-list-item-title>Day</v-list-item-title>
              </v-list-item>
              <v-list-item @click="type = 'week'">
                <v-list-item-title>Week</v-list-item-title>
              </v-list-item>
              <v-list-item @click="type = 'month'">
                <v-list-item-title>Month</v-list-item-title>
              </v-list-item>
              <v-list-item @click="type = '4day'">
                <v-list-item-title>4 days</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-toolbar>
      </div>

      <template>
        <v-dialog v-model="dialog" persistent width="60%">
          <v-card>
            <v-card-title
                class="headline font-weight-regular blue-grey white--text"
                primary-title
            >{{ title }}

              <v-spacer></v-spacer>

              <v-btn color="white" text @click="closeDialog()">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title
            >
            <v-divider></v-divider>
            <HrdAddProgramEvent
                v-if="dialog"
                :programEvent="programEvent"
                @close="dialog = false"
                @regsiterProgramEvent="regsiterProgramEvent"
            ></HrdAddProgramEvent>
          </v-card>
        </v-dialog>
      </template>

      <div v-if="eventLoading" class="program-event-loading">
        <v-progress-circular
            :size="50"
            :width="4"
            color="primary"
            indeterminate
        ></v-progress-circular>
      </div>
      <v-sheet
          v-else
          :height="calendarHeight"
          :class="['program-event-calendar', { 'program-event-calendar--auto': type !== 'month' }]"
      >
        <v-calendar
            ref="calendar"
            v-model="focus"
            :event-color="getEventColor"
            :events="filteredEvents"
            :type="type"
            color="primary"
            @click:event="showEvent"
            @click:more="viewDay"
            @click:date="viewDay"
            @change="onCalendarChange"
        >
          <template #event="{ event }">
            <EventComponent :event="event"/>
          </template>
        </v-calendar>
        <v-menu
            v-model="selectedOpen"
            :activator="selectedElement"
            :close-on-content-click="false"
            offset-x
        >
          <v-card color="grey lighten-4" flat min-width="350px" class="program-event-detail">
            <v-toolbar :color="selectedEvent.color" dark>
              <v-toolbar-title
                  v-html="`${selectedEvent.program ? (selectedEvent.program.category ? selectedEvent.program.category.name : null) : null}`"></v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon @click="selectedOpen = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card flat>
              <v-card-text class="program-event-detail__body">

                <div class="flex my-4 mb-4">
                  <div class="w-1/3 h-12">
                    <p class="text-md-left font-weight-bold">CPD Activity:</p>
                  </div>
                  <div class="w-1/3 h-12">
                    <div class=".text-base">
                      {{
                      selectedEvent.program
                      ? selectedEvent.program.category
                      ? selectedEvent.program.category.name
                      : "-"
                      : ""
                      }}
                    </div>
                  </div>
                </div>
                <div
                    v-if="selectedCategoryType == 'CAT1'"
                    class="flex my-4 mb-4"
                >
                  <div class="w-1/3 h-12">
                    <p class="text-md-left font-weight-bold">Courses:</p>
                  </div>
                  <div
                      v-for="item in selectedEvent.program.program_courses"
                      :key="item.id"
                      class="w-1/3 h-12"
                  >
                    <div class=".text-base">
                      {{ item.course ? item.course.courseName : "-" }},
                    </div>
                  </div>
                </div>
                <div
                    v-if="selectedCategoryType == 'CAT2'"
                    class="flex my-4 mb-4"
                >
                  <div class="w-1/3 h-12">
                    <p class="text-md-left font-weight-bold">Courses:</p>
                  </div>
                  <div class="w-1/3 h-12">
                    <div class=".text-base">
                      {{
                      selectedEvent.program.courseTitleCat2
                      ? selectedEvent.program.courseTitleCat2
                      : "-"
                      }}
                    </div>
                  </div>
                </div>
                <div class="flex my-4 mb-4">
                  <div class="w-1/3">
                    <p class="text-md-left font-weight-bold">Funders:</p>
                  </div>
                  <div class="w-2/3">
                    <div v-if="selectedEvent.funders && selectedEvent.funders.length">
                      <div v-for="funder in selectedEvent.funders" :key="funder.id">
                        {{ funder.organization ? funder.organization.name : "-" }} —
                        {{ formatFundAmount(funder.fundAmount) }}
                      </div>
                    </div>
                    <div v-else>—</div>
                  </div>
                </div>
                <div class="flex my-4 mb-4">
                  <div class="w-1/3 h-12">
                    <p class="text-md-left font-weight-bold">
                      Event Start Date(EC):
                    </p>
                  </div>
                  <div class="w-1/3 h-12">
                    <div class=".text-base">
                      {{ selectedEvent.etStart ? selectedEvent.etStart : "" }}
                    </div>
                  </div>
                </div>
                <div class="flex my-4 mb-4">
                  <div class="w-1/3 h-12">
                    <p class="text-md-left font-weight-bold">
                      Event End Date(EC):
                    </p>
                  </div>
                  <div class="w-1/3 h-12">
                    <div class=".text-base">
                      {{ selectedEvent.etEnd ? selectedEvent.etEnd : "" }}
                    </div>
                  </div>
                </div>
                <div v-if="false" class="flex my-4 mb-4">
                  <div class="w-1/3 h-12">
                    <p class="text-md-left font-weight-bold">Funded By:</p>
                  </div>
                  <div class="w-1/3 h-12">
                    <div class=".text-base">
                      {{
                      selectedEvent.fundedBy
                      ? selectedEvent.fundedBy.name
                      : "-"
                      }}
                    </div>
                  </div>
                </div>
                <div class="flex my-4 mb-4">
                  <div class="w-1/3 h-12">
                    <p class="text-md-left font-weight-bold">Total Cost:</p>
                  </div>
                  <div class="w-1/3 h-12">
                    <div class=".text-base">
                      {{
                      selectedEvent.totalCost ? selectedEvent.totalCost : "-"
                      }}
                    </div>
                  </div>
                </div>
                <div class="flex my-4 mb-4">
                  <div class="w-1/3 h-12">
                    <p class="text-md-left font-weight-bold">CPD Provider:</p>
                  </div>
                  <div class="w-1/3 h-12">
                    <div class=".text-base">
                      {{
                      selectedEvent.trainingCenter ? selectedEvent.trainingCenter.trainingCenterName : "-"
                      }}
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn v-if="canEditEvent" color="secondary" text @click="editEvent(selectedEvent)">
                Edit
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </v-sheet>
    </v-col>
  </v-row>
</template>

<script>
import HrdAddProgramEvent from "./hrd-add-program-event";
import {ProgramEventService} from "./programevent.service";
import ethiopic from "ethiopic-calendar";
import {getUser} from "@/utils/helper";
import EventComponent from "./event-component.vue";
import {TrainingCenterService} from "../TrainingCenter/training_center.service";
import {MfrService} from "../Lookups/mfr/mfr.service";

export default {
  components: {
    HrdAddProgramEvent,
    EventComponent
  },
  data: () => ({
    today: new Date().toISOString().substring(0, 10),
    focus: new Date().toISOString().substring(0, 10),
    type: "month",
    typeToLabel: {
      month: "month",
      week: "Week",
      day: "Day",
      "4day": "4 Days",
    },
    name: null,
    details: null,
    start: null,
    end: null,
    color: "#1976D2",
    title: "",
    //during edit event which event should be editted
    currentlyEditing: null,
    selectedEvent: {},
    //related to DOM
    selectedElement: null,
    selectedOpen: false,
    events: [],
    dialog: false,
    editDialog: false,
    programEvent: null,
    selectedCategoryType: null,
    eventLoading: false,
    cpdProviderLoading: false,
    cpdProviders: [],
    cpdProviderValue: null,
    regionLoading: false,
    regions: [],
    regionValue: null,
    yearValue: new Date().getFullYear(),
    yearOptions: [],
    monthValue: new Date().getMonth(),
    lastFetchedKey: null,
    rangeStart: null,
    rangeEnd: null,
    monthOptions: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
  }),
  beforeMount() {
    this.loadCpdProviders();
    this.loadRegions();
    this.syncSelectorsFromFocus(this.focus);
    this.yearOptions = this.buildYearOptions();
  },
  mounted() {
    this.refreshEvents(true);
  },
  computed: {
    canCreateEvent() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Program Event" && permission.canCreate
      );
    },
    canEditEvent() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Program Event" && permission.canUpdate
      );
    },
    filteredEvents() {
      const baseEvents = Array.isArray(this.events) ? this.events : [];
      return baseEvents.filter((event) => {
        if (this.regionValue) {
          const eventRegionId =
              event.trainingCenter?.region_id ||
              event.trainingCenter?.regionId ||
              event.trainingCenter?.region?.id;
          if (String(eventRegionId) !== String(this.regionValue)) {
            return false;
          }
        }
        if (!this.cpdProviderValue) {
          return true;
        }
        const providerId = event.trainingCenter?.id || event.trainingCenterId || event.cpdProviderId;
        return providerId === this.cpdProviderValue;
      });
    },
    currentMonthLabel() {
      const month = this.monthOptions[this.monthValue] || "Month";
      const year = this.yearValue || "";
      return `${month} ${year}`.trim();
    },
    calendarHeight() {
      return this.type === "month" ? 700 : undefined;
    },
  },
  watch: {
    focus(newValue) {
      this.syncSelectorsFromFocus(newValue);
    },
    yearValue() {
      this.syncFocusFromSelectors();
    },
    monthValue() {
      this.syncFocusFromSelectors();
    },
    regionValue() {
      this.refreshEvents(true);
    },
  },
  methods: {
    formatFundAmount(value) {
      if (value === null || value === undefined || value === "") return "-";
      const amount = Number(value);
      return Number.isFinite(amount) ? amount.toLocaleString() : value;
    },
    parseFocus(value) {
      if (!value || typeof value !== "string") return null;
      const [year, month] = value.split("-").map(Number);
      if (!year || !month) return null;
      return {year, monthIndex: month - 1};
    },
    syncSelectorsFromFocus(value) {
      const parsed = this.parseFocus(value);
      if (!parsed) return;
      if (this.yearValue !== parsed.year) {
        this.yearValue = parsed.year;
      }
      if (this.monthValue !== parsed.monthIndex) {
        this.monthValue = parsed.monthIndex;
      }
    },
    syncFocusFromSelectors() {
      if (this.yearValue == null || this.monthValue == null) return;
      const month = String(this.monthValue + 1).padStart(2, "0");
      const newFocus = `${this.yearValue}-${month}-01`;
      if (this.focus !== newFocus) {
        this.focus = newFocus;
      }
    },
    buildYearOptions() {
      const currentYear = new Date().getFullYear();
      const years = new Set([
        currentYear - 2,
        currentYear - 1,
        currentYear,
        currentYear + 1,
        currentYear + 2,
      ]);
      if (Number.isFinite(this.yearValue)) {
        years.add(this.yearValue);
      }
      (this.events || []).forEach((event) => {
        const year = Number((event.start || "").toString().split("-")[0]);
        if (Number.isFinite(year) && year > 1900) {
          years.add(year);
        }
      });
      return Array.from(years).sort((a, b) => a - b);
    },
    getFocusYearMonth() {
      const parsed = this.parseFocus(this.focus);
      if (parsed) {
        return {year: parsed.year, month: parsed.monthIndex + 1};
      }
      if (this.yearValue != null && this.monthValue != null) {
        return {year: this.yearValue, month: this.monthValue + 1};
      }
      const today = new Date();
      return {year: today.getFullYear(), month: today.getMonth() + 1};
    },
    async fetchEventsForRange(start, end, force = false) {
      if (!start || !end) return;
      const startDate = typeof start === "string" ? start : start.date;
      const endDate = typeof end === "string" ? end : end.date;
      const key = `${startDate}-${endDate}`;
      if (!force && this.lastFetchedKey === key) return;
      this.lastFetchedKey = key;
      await this.getEvents({startDate, endDate});
    },
    refreshEvents(force = false) {
      if (this.rangeStart && this.rangeEnd) {
        return this.fetchEventsForRange(this.rangeStart, this.rangeEnd, force);
      }
      const {year, month} = this.getFocusYearMonth();
      return this.getEvents({year, month});
    },
    onCalendarChange({start, end}) {
      this.rangeStart = start;
      this.rangeEnd = end;
      this.fetchEventsForRange(start, end);
    },
    async loadCpdProviders() {
      this.cpdProviderLoading = true;
      const trainingCenterResp = await TrainingCenterService.getAllTrainingCenters();
      const data = trainingCenterResp?.data || trainingCenterResp;
      const providers = data?.rows || data?.data || data || [];
      this.cpdProviders = Array.isArray(providers)
          ? providers.sort((a, b) => (a?.trainingCenterName || "").localeCompare(b?.trainingCenterName || ""))
          : [];
      this.cpdProviderLoading = false;
    },
    async loadRegions() {
      this.regionLoading = true;
      const regionResp = await MfrService.getRegionsDrp();
      const regions = regionResp?.data || [];
      this.regions = Array.isArray(regions)
          ? regions.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
          : [];
      this.regionLoading = false;
    },
    convertGE(val) {
      if (!val) return val;
      const [year, month, day] = val.split("-").map(Number);
      if (this.pickerType === "year") {
        let etDate = ethiopic.ge(year, month || 6, day || 1); // so it will be the same year in both directions
        return etDate.year.toString().padStart(4, "0");
      } else {
        let etDate = ethiopic.ge(year, month || 1, day || 1);
        return (
            etDate.year.toString().padStart(4, "0") +
            "-" +
            etDate.month.toString().padStart(2, "0") +
            "-" +
            etDate.day.toString().padStart(2, "0")
        );
      }
    },
    convertEG(val) {
      if (!val) return val;
      const [etYear, etMonth, etDay] = val.split("-").map(Number);
      if (this.pickerType === "year") {
        let gDate = ethiopic.eg(etYear, etMonth || 6, etDay || 1); // so it will be the same year in both directions
        return gDate.year.toString().padStart(4, "0");
      } else {
        let gDate = ethiopic.eg(etYear, etMonth || 1, etDay || 1);
        return (
            gDate.year.toString().padStart(4, "0") +
            "-" +
            gDate.month.toString().padStart(2, "0") +
            "-" +
            gDate.day.toString().padStart(2, "0")
        );
      }
    },
    async getEvents(params = {}) {
      this.eventLoading = true;
      const requestParams = {...params};
      if (this.regionValue) {
        requestParams.regionId = this.regionValue;
      }
      const res = await ProgramEventService.getProgramEvents(requestParams);
      if (res.status === 200) {
        const payload = res.data;
        this.events = Array.isArray(payload) ? payload : payload?.rows || payload?.data || [];
        this.events.forEach((element) => {
          element.programName = element.program ? (element.program.category ? element.program.category.name : null) : null
          element.etStart = element.start;
          // element.start = this.convertEG(element.start);
          element.etEnd = element.end;
          // element.end = this.convertEG(element.end);
          element.cpdProvider = element.trainingCenter ? element.trainingCenter.trainingCenterName : null;
        });
        this.yearOptions = this.buildYearOptions();
      } else {
        this.$toast.error("Unable to fetch program events");
      }
      this.eventLoading = false;
    },
    viewDay({date}) {
      this.focus = date;
      this.type = "day";
    },

    getEventColor(event) {
      return event.color;
    },
    setToday() {
      this.focus = this.today;
      this.type = "day"
    },
    prev() {
      this.$refs.calendar.prev();
    },
    next() {
      this.$refs.calendar.next();
    },
    showEvent({nativeEvent, event}) {
      const open = () => {
        this.selectedEvent = event;
        if (this.selectedEvent.program && this.selectedEvent.program.category) {
          this.selectedCategoryType = this.selectedEvent.program.category.categoryType;
        }
        this.selectedElement = nativeEvent.target;
        setTimeout(() => {
          this.selectedOpen = true;
        }, 10);
      };

      if (this.selectedOpen) {
        this.selectedOpen = false;
        setTimeout(open, 10);
      } else {
        open();
      }

      nativeEvent.stopPropagation();
    },
    closeDialog() {
      this.dialog = false;
      this.refreshEvents(true);
    },
    editEvent(event) {
      this.programEvent = event;
      this.dialog = true;
      this.title = "Update Program Event";
      this.currentlyEditing = event.id;
      this.selectedOpen = false;
    },
    newEvent() {
      this.programEvent = null;
      this.dialog = true;
      this.title = "Add Program Event";
    },
    async regsiterProgramEvent(programEvent) {
      let res = null;

      if (programEvent.editMode) {
        res = await ProgramEventService.update(programEvent.id, programEvent);

        if (res.status === 201) {
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.refreshEvents(true);
              break;
            case 500:
              this.$toast.error(res.data.message);
              break;
          }
        } else {
          this.$toast.error("Unable to update program event, please try again");
        }
      } else {
        res = await ProgramEventService.create(programEvent);

        if (res.status === 200) {
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.refreshEvents(true);

              break;
            case 500:
              this.$toast.error(res.data.message);
              break;
          }
        } else {
          this.$toast.error("Unable to create program event, please try again");
        }
      }
    },
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap");

.program-event-page {
  --pe-bg: #f5f7fb;
  --pe-card: #ffffff;
  --pe-border: #e3e7ef;
  --pe-text: #1f2933;
  --pe-muted: #667085;
  --pe-accent: #2563eb;
  background: radial-gradient(1200px 500px at 10% 0%, #eef2ff 0%, #f5f7fb 55%, #f8fafc 100%);
  font-family: "Plus Jakarta Sans", "Poppins", "Segoe UI", sans-serif;
  padding: 16px;
}

.program-event-shell {
  background: var(--pe-card);
  border: 1px solid var(--pe-border);
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.08);
  margin-bottom: 16px;
}

.program-event-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.program-event-hero__title {
  font-size: 22px;
  font-weight: 700;
  color: var(--pe-text);
  letter-spacing: -0.02em;
}

.program-event-hero__subtitle {
  color: var(--pe-muted);
  font-size: 13px;
  margin-top: 4px;
}

.program-event-hero__filter {
  min-width: 240px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.program-event-filter .v-input__slot {
  background: #f8fafc;
  border-radius: 12px;
}

.program-event-filter--year {
  max-width: 140px;
}

.program-event-toolbar {
  margin-top: 12px;
  padding: 10px 6px;
  border-radius: 14px;
  border: 1px solid var(--pe-border);
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
}

.program-event-toolbar__title {
  font-weight: 600;
  color: var(--pe-text);
}

.program-event-toolbar__title-btn {
  font-weight: 600;
  color: var(--pe-text);
  text-transform: none;
  letter-spacing: -0.01em;
}

.program-event-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
}

.program-event-calendar {
  border-radius: 18px;
  border: 1px solid var(--pe-border);
  overflow: hidden;
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.06);
}

.program-event-page .v-calendar .v-event {
  height: 52px !important;
}

.program-event-page .v-calendar-weekly__week {
  min-height: 160px !important;
}

.program-event-page .vc-highlight {
  height: 400px;
}

.program-event-calendar .v-calendar {
  height: 100%;
}

.program-event-calendar .v-calendar-daily__body,
.program-event-calendar .v-calendar-daily__scroll-area,
.program-event-calendar .v-calendar-daily__pane {
  height: 100%;
}

.program-event-calendar .v-calendar-daily__scroll-area {
  overflow-y: auto;
}

.program-event-calendar--auto {
  height: auto !important;
  max-height: none;
}

.program-event-calendar--auto .v-calendar,
.program-event-calendar--auto .v-calendar-daily {
  height: auto;
}

.program-event-detail {
  max-height: 520px;
  display: flex;
  flex-direction: column;
}

.program-event-detail__body {
  max-height: 420px;
  overflow-y: auto;
  padding-right: 12px;
}
</style>
